import { computed, onBeforeUnmount, ref, unref, watch, type Ref } from "vue";
import { ensureFreshAccessToken, recoverFromSessionError } from "@/keycloak/AuthenticationSession";

type SignedUrlResponse = {
  url: string;
  expiresAt: string;
};

type CacheEntry = {
  expiresAt: number;
  promise: Promise<string>;
};

const signedUrlCache = new Map<string, CacheEntry>();
const expiryBufferMs = 5 * 60 * 1000;

function cacheKey(uuid: string) {
  return uuid;
}

export function invalidateMinioSignedUrl(uuid?: string | null) {
  if (!uuid) {
    return;
  }

  signedUrlCache.delete(cacheKey(uuid));
}

export async function getMinioSignedUrl(uuid?: string | null) {
  if (!uuid) {
    return undefined;
  }
  const fileUuid = uuid;

  const now = Date.now();
  const key = cacheKey(uuid);
  const cached = signedUrlCache.get(key);

  if (cached != null && cached.expiresAt - expiryBufferMs > now) {
    return cached.promise;
  }

  async function requestSignedUrl(authenticationRetried = false): Promise<Response> {
    const accessToken = await ensureFreshAccessToken();
    const response = await fetch(`/api/minio/signed/${encodeURIComponent(fileUuid)}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.status === 401 && !authenticationRetried) {
      await recoverFromSessionError();
      return requestSignedUrl(true);
    }

    return response;
  }

  const promise = requestSignedUrl()
    .then(async (response) => {
      if (!response.ok) {
        signedUrlCache.delete(key);
        throw new Error(`Could not resolve signed file URL for ${uuid}`);
      }

      const payload = (await response.json()) as SignedUrlResponse;
      const expiresAt = Date.parse(payload.expiresAt);

      signedUrlCache.set(key, {
        expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now(),
        promise: Promise.resolve(payload.url),
      });

      return payload.url;
    })
    .catch((error: unknown) => {
      signedUrlCache.delete(key);
      throw error;
    });

  signedUrlCache.set(key, {
    expiresAt: now + 60 * 1000,
    promise,
  });

  return promise;
}

export function useMinioSignedUrl(uuid: Ref<string | undefined | null>) {
  const url = ref<string>();
  const loading = ref(false);
  const error = ref<unknown>();
  let token = 0;

  async function load() {
    const currentUuid = unref(uuid);
    const currentToken = ++token;

    url.value = undefined;
    error.value = undefined;

    if (!currentUuid) {
      loading.value = false;
      return;
    }

    loading.value = true;

    try {
      const resolvedUrl = await getMinioSignedUrl(currentUuid);
      if (currentToken === token) {
        url.value = resolvedUrl;
      }
    } catch (loadError: unknown) {
      if (currentToken === token) {
        error.value = loadError;
      }
    } finally {
      if (currentToken === token) {
        loading.value = false;
      }
    }
  }

  watch(uuid, load, { immediate: true });

  onBeforeUnmount(() => {
    token++;
  });

  return {
    url: computed(() => url.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    reload: load,
  };
}
